import React, { useContext, useMemo, useState } from 'react';
import { TranslationKey } from './../../../../types/translation/TranslationKey';
import { t } from '../../../../types/translation/Translator';
import { useNavigate } from 'react-router-dom';
import { ProductContext } from '../../../../context/ProductContext';
import { Product } from '../../../../types/product';
import ProductImportModal from './Modals/ProductImportModal';
import { ProductMasterDataContext } from '../../../../context/ProductMasterDataContext';
import { CompanyContext } from '../../../../context/CompanyContext';
import { StockLocationRole } from '../../../../types/stock_location_role_assignment/stockLocationRoleAssignment';
import { CompanyRoleAssignmentContext } from '../../../../context/CompanyRoleAssignmentContext';
import { StockLocationRoleAssignmentContext } from '../../../../context/StockLocationRoleAssignmentContext';
import { UserContext } from '../../../../context/UserContext';
import { SuperUserContext } from '../../../../context/SuperUserContext';
import { testIds } from '../../../../util/identifiers/identifiers.util';
import { ProductExportModal } from './Modals/ProductExportModal';
import Table from '../../../components/Table';
import { ProductFilter } from '../../../../VentoryUI/components/filters/ProductFilter';
import { BinStatusContext } from '../../../../context/BinStatusContext';
import { TagRelationContext } from '../../../../context/TagRelationContext';
import { BinContext } from '../../../../context/BinContext';
import { CustomFieldContext } from '../../../../context/CustomFieldContext';
import { Routing } from '../../../../util/routes.util';
import SearchBarWithFilter from '../../../../VentoryUI/components/common/SearchBarWithFilter';
import { SettingsIconButton } from '../../../icons';
import { HandlingUnitContext } from '../../../../context/HandlingUnitContext';
import { productTableHeaders } from '../../../components/Table/Product/ProductTable';
import { ProductFilterType } from '../../../../VentoryUI/components/filters/ProductFilter/ProductFilter';
import { AddButton, ExportIconButton, ImportIconButton } from '../../../components/Button';
import QuickActionModal from './Detail/Info/QuickActionModal';
import { ProductMasterData } from '../../../../types/product_master_data/productMasterData';
import { SystemStockLocation } from '../../../../types/stock_location/stockLocation';
import { TableSettingsContext } from '../../../../context/TableSettingsContext';
import { EntityType } from '../../../../types/comment';
import { StockLocationContext } from '../../../../context/StockLocationContext';
import { ContainerContext } from '../../../../context/ContainerContext';
import { ContainerTypeContext } from '../../../../context/ContainerTypeContext';

export default function ProductOverviewPane() {
  const navigate = useNavigate();

  const { currentCompany, companies } = useContext(CompanyContext);
  const { products, productsLoading } = useContext(ProductContext);
  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { bins } = useContext(BinContext);
  const { binStatuses } = useContext(BinStatusContext);
  const { companyRoles, isAdmin } = useContext(CompanyRoleAssignmentContext);
  const { getTableSettings } = useContext(TableSettingsContext);
  const { currentUser, locale } = useContext(UserContext);
  const { hasStockLocationRoleInCompany } = useContext(StockLocationRoleAssignmentContext);
  const { superUser } = useContext(SuperUserContext);
  const { tagRelations } = useContext(TagRelationContext);
  const { customFields } = useContext(CustomFieldContext);
  const { handlingUnits } = useContext(HandlingUnitContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { containers } = useContext(ContainerContext);
  const { containerTypes } = useContext(ContainerTypeContext);

  const [importModalOpen, setImportModalOpen] = useState<boolean>(false);
  const [exportModalOpen, setExportModalOpen] = useState<boolean>(false);
  const [quickActionModalOpen, setQuickActionModalOpen] = useState<boolean>(false);

  const productTableSettings = getTableSettings(EntityType.product, currentCompany.id);
  const containerTableSettings = getTableSettings(EntityType.container, currentCompany.id);

  const placeholderProduct = (pmd: ProductMasterData) => {
    return new Product({
      companyId: currentCompany.id,
      productMasterDataId: pmd.id,
      unitQuantity: BigInt(0),
      stockLocationId: SystemStockLocation.id,
      binId: '',
    });
  };

  const displayEmptyStock = currentCompany.settings.featureToggles.products.displayEmptyStock;

  const allProducts = useMemo(() => {
    const items: Product[] = [...products.values()].flatMap(itemMap => Array.from(itemMap.values()));

    if (displayEmptyStock) {
      const emptyProducts = Array.from(productMasterData.values()).map(
        pmd =>
          new Product({
            companyId: currentCompany.id,
            productMasterDataId: pmd.id,
            unitQuantity: BigInt(0),
            stockLocationId: SystemStockLocation.id,
          }),
      );
      items.push(...emptyProducts);
    }

    const hus = Array.from(handlingUnits.values());
    const actualProducts: Product[] = [];
    for (const item of items) {
      const pmd = productMasterData.get(item.productMasterDataId);
      if (!pmd) continue;

      if (pmd.handlingUnit) {
        const hu = hus.find(hu => hu.parentId === pmd.id);
        if (!hu) {
          actualProducts.push(item);
        } else if (hu.default) {
          actualProducts.push(item);
        } else {
          actualProducts.push(Product.forHandlingUnit(item, hu));
        }
        continue;
      }

      actualProducts.push(item);
    }

    return actualProducts;
  }, [products, productMasterData, handlingUnits]);

  const allItems = useMemo(() => {
    return allProducts;
  }, [allProducts]);

  const [items, setItems] = useState<Product[]>(allItems);

  const itemProducts = new Set(items.map(i => i.id));

  const productQuantity = () => {
    const items = allProducts;

    const quantity: Map<string, Product> = new Map();
    for (const item of items) {
      const pmd = productMasterData.get(item.productMasterDataId);
      if (!pmd) continue;

      if (!itemProducts.has(item.id)) continue;

      const current: Product = quantity.get(item.productMasterDataId) || placeholderProduct(pmd);
      current.unitQuantity = (BigInt(current.unitQuantity) + BigInt(item.unitQuantity)).toString();
      quantity.set(item.productMasterDataId, current);
    }
    return quantity;
  };

  const isManager = hasStockLocationRoleInCompany(currentCompany.id, StockLocationRole.STOCK_LOCATION_MANAGER);

  const productValues = useMemo(() => {
    return new Map(
      [...products.values()]
        .flatMap(i => [...i.values()])
        .flat()
        .map(i => [i.id, i]),
    );
  }, [products]);

  const filter = new ProductFilter(
    stockLocations,
    productMasterData,
    bins,
    binStatuses,
    containers,
    containerTypes,
    tagRelations,
    customFields,
    productValues,
    productTableSettings,
    containerTableSettings,
    ProductFilterType.group,
    currentCompany,
  );

  return (
    <>
      {importModalOpen && <ProductImportModal open={importModalOpen} setOpen={setImportModalOpen} />}
      {exportModalOpen && <ProductExportModal open={exportModalOpen} setOpen={setExportModalOpen} />}
      {quickActionModalOpen && (
        <QuickActionModal open={quickActionModalOpen} setOpen={setQuickActionModalOpen} props={{}} />
      )}

      <div className='flex flex-grow flex-col gap-4'>
        <div className='flex justify-between gap-2'>
          <SearchBarWithFilter
            loading={productsLoading || productMasterDataLoading}
            items={allItems}
            setItems={setItems}
            placeholder={t(TranslationKey.filterProducts).singular.label}
            testId={testIds.products}
            filter={filter}
          />
          <div className='flex gap-2'>
            {isManager && <ImportIconButton onClick={() => setImportModalOpen(true)} />}
            {items.length > 0 && <ExportIconButton onClick={() => setExportModalOpen(true)} />}
            <AddButton onClick={() => setQuickActionModalOpen(true)} />
            {isAdmin ? (
              <SettingsIconButton onClick={() => navigate(Routing.stock.products.settings.overview())} />
            ) : null}
          </div>
        </div>
        <Table
          filter={filter}
          loading={productsLoading || productMasterDataLoading}
          title={t(TranslationKey.product).plural.label}
          headers={productTableHeaders(
            productTableSettings,
            productMasterData,
            productQuantity(),
            companies,
            currentCompany,
            locale,
          )}
          totalItemCount={displayEmptyStock ? productMasterData.size : [...products.keys()].length}
          onClick={pmdId => navigate(Routing.stock.products.detail(pmdId))}
          items={[
            ...new Set(
              items
                .map(item => item.productMasterDataId)
                .filter(item => productMasterData.get(item))
                .values(),
            ),
          ]}
        />
      </div>
    </>
  );
}
